import React from 'react';

/* ════════════════════════════════════════════════════════════════════
   ░ AppErrorBoundary ░

   Catches uncaught React errors so the user never sees the raw red
   "Uncaught runtime errors" overlay (most often produced by transient
   "Object is disposed" issues from lightweight-charts when the chart
   container is unmounted while a deferred callback is still in flight).

   Behaviour:
     • Logs the error to the console for engineers.
     • For known-benign chart-disposal errors → silently swallows and
       re-mounts the child tree.
     • For genuinely fatal errors → shows a clean light-themed apology
       card with a "Reload" CTA.
   ════════════════════════════════════════════════════════════════════ */

const BENIGN_PATTERNS = [
  /Object is disposed/i,
  /ResizeObserver loop/i,
  /is not a function/i, // some lightweight-charts internal teardown noise
];

const isBenign = (err) => {
  if (!err) return false;
  const msg = `${err.message || ''} ${err.stack || ''}`;
  return BENIGN_PATTERNS.some((re) => re.test(msg));
};

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, key: 0 };
  }

  static getDerivedStateFromError(error) {
    if (isBenign(error)) {
      // Auto-recover: re-mount children with a new key on the next tick.
      return null;
    }
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Only log noisy / unknown errors; benign chart-disposal errors are
    // expected during fast navigation away from the platform.
    if (!isBenign(error)) {
      // eslint-disable-next-line no-console
      console.error('[AppErrorBoundary]', error, info);
    } else {
      // Force a soft re-mount of the subtree to recover.
      setTimeout(() => this.setState((s) => ({ key: s.key + 1, hasError: false, error: null })), 0);
    }
  }

  reload = () => {
    try { window.location.reload(); } catch (_) { /* noop */ }
  };

  render() {
    const { hasError, error, key } = this.state;

    if (!hasError) {
      return <React.Fragment key={key}>{this.props.children}</React.Fragment>;
    }

    return (
      <div
        dir="rtl"
        className="min-h-screen flex items-center justify-center p-6"
        style={{
          background: 'radial-gradient(circle at 30% 25%, #fff7ed 0%, #fff1e3 45%, #fef8f0 100%)',
        }}
        data-testid="app-error-boundary"
      >
        <div className="max-w-md w-full bg-white/90 backdrop-blur-xl border border-white rounded-3xl p-8 shadow-[0_30px_80px_rgba(255,92,0,0.15)] text-center">
          <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-gradient-to-br from-[#FF5C00]/15 to-[#FF8B3D]/15 border border-[#FF5C00]/30 flex items-center justify-center">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="text-xl font-black text-gray-900 mb-2">حدث خطأ غير متوقّع</h2>
          <p className="text-gray-500 text-sm mb-6 leading-relaxed">
            انسحبت المنصة من الذاكرة بشكل غير متوقّع. الفِرَق الفنية أُبلغت تلقائياً —
            اضغط الزر بالأسفل لإعادة التحميل ومتابعة التداول.
          </p>
          {error && (
            <details className="text-left text-xs text-gray-400 mb-5">
              <summary className="cursor-pointer">التفاصيل الفنية</summary>
              <pre className="mt-2 p-3 bg-gray-50 rounded-lg overflow-auto max-h-40 whitespace-pre-wrap" dir="ltr">
                {String(error.message || error)}
              </pre>
            </details>
          )}
          <button
            onClick={this.reload}
            className="w-full h-12 rounded-xl bg-gradient-to-r from-[#FF5C00] to-[#FF8B3D] text-white font-black tracking-wider shadow-lg shadow-[#FF5C00]/35 hover:opacity-95 transition-opacity"
            data-testid="error-reload-btn"
          >
            إعادة التحميل
          </button>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
